// O menu do console em GRUPOS. É a única fonte do menu: a barra lateral, a barra do celular, a gaveta
// "Menu" e as migalhas leem daqui.
//
// O menu mostra só o que o PAPEL permite (`capacidade`, ver lib/papeis.ts). Isso é apresentação: toda
// rota da API tem guarda no servidor.
//
// Branch web: Início (W1), Comercial — Contratantes e Catálogo (W2), Contratos (W3) —, Suporte — Clientes,
// Acessos (W5) e Instaladores —, Cadastros — Cadastros globais e Integrações (W4), PlugAI (D3) —, Equipe e Auditoria (W1) e Servidores (monitoramento).
//
// O portal mora na RAIZ de admin.plugchef.com.br: `/` é o login e as telas ficam em `/inicio`,
// `/equipe`… NENHUMA tela pode começar com `/admin/v1` — o Caddy manda esse caminho para a API.
import type { ComponentType } from 'react';
import type { Capacidade } from '../../lib/papeis';
import {
  IconAi, IconClientes, IconCpu, IconDocLines, IconDownload, IconEye, IconGrid, IconHelp, IconLock, IconPriceTag, IconUsersGroup, type IconProps,
} from '../desktop/icones';
import { IconHoje, IconRede } from './iconesConsole';

export interface ItemConsole {
  id: string;
  rotulo: string;
  para: string;
  icone: ComponentType<IconProps>;
  /** null = todo mundo da equipe. */
  capacidade: Capacidade | null;
  /** Uma linha sobre o que há na tela. */
  descricao: string;
  /** Outros caminhos que acendem este item. */
  tambem?: string[];
}

export interface GrupoConsole {
  id: string;
  /** null = sem título (o topo do menu). */
  titulo: string | null;
  itens: ItemConsole[];
}

/** A tela de entrada depois do login. */
export const INICIO = '/inicio';

/** O login (a raiz do domínio). */
export const LOGIN = '/';

export const GRUPOS: GrupoConsole[] = [
  {
    id: 'inicio',
    titulo: null,
    itens: [{ id: 'inicio', rotulo: 'Início', para: INICIO, icone: IconHoje, capacidade: null, descricao: 'Boas-vindas e o que o seu papel permite' }],
  },
  {
    id: 'comercial',
    titulo: 'Comercial',
    itens: [
      {
        id: 'contratantes',
        rotulo: 'Contratantes',
        para: '/comercial/contratantes',
        icone: IconClientes,
        capacidade: 'comercial.ver',
        descricao: 'Quem contrata (ou vai contratar) e a criação do cliente',
      },
      {
        id: 'contratos',
        rotulo: 'Contratos',
        para: '/comercial/contratos',
        icone: IconDocLines,
        capacidade: 'comercial.ver',
        descricao: 'Um contrato por empresa: as faturas, a cobrança e as alterações',
      },
      {
        id: 'catalogo',
        rotulo: 'Catálogo',
        para: '/comercial/catalogo',
        icone: IconPriceTag,
        capacidade: 'comercial.ver',
        descricao: 'Planos, módulos, adicionais e os ciclos de cobrança',
      },
    ],
  },
  {
    id: 'suporte',
    titulo: 'Suporte',
    itens: [
      {
        id: 'suporte-clientes',
        rotulo: 'Clientes',
        para: '/suporte',
        icone: IconHelp,
        capacidade: 'suporte.ver',
        descricao: 'Abrir um acesso a um cliente (com o motivo) para ver os dados dele ou configurar a loja',
      },
      {
        id: 'suporte-acessos',
        rotulo: 'Acessos',
        para: '/suporte/acessos',
        icone: IconLock,
        capacidade: 'suporte.ver',
        descricao: 'Os acessos de suporte abertos e encerrados',
      },
      {
        id: 'suporte-instaladores',
        rotulo: 'Instaladores',
        para: '/suporte/instaladores',
        icone: IconDownload,
        capacidade: 'suporte.ver',
        descricao: 'A última versão do PlugChef Terminal (e dos outros programas da loja) para baixar à mão',
      },
    ],
  },
  {
    id: 'produto',
    titulo: 'Produto',
    itens: [
      {
        id: 'melhorias',
        rotulo: 'Melhorias',
        para: '/melhorias',
        icone: IconDocLines,
        capacidade: 'melhorias.ver',
        descricao: 'O que as lojas sugerem e votam: a fila de aprovação, a resposta e o andamento',
      },
    ],
  },
  {
    id: 'cadastros',
    titulo: 'Cadastros',
    itens: [
      {
        id: 'globais',
        rotulo: 'Cadastros globais',
        para: '/cadastros/globais',
        icone: IconGrid,
        capacidade: 'globais.ver',
        descricao: 'Unidades, CFOP, cidades, estados e países: os mesmos para todas as lojas',
      },
      {
        id: 'integracoes',
        rotulo: 'Integrações',
        para: '/cadastros/integracoes',
        icone: IconRede,
        capacidade: 'aplicativos.ver',
        descricao: 'Os aplicativos que as lojas conectam e as credenciais da Resulti nos parceiros',
      },
      {
        id: 'plugai',
        rotulo: 'PlugAI',
        para: '/cadastros/plugai',
        icone: IconAi,
        capacidade: 'plugai.ver',
        descricao: 'A IA nas lojas: motores, pacotes, checks, agentes, a carteira de cada cliente e o consumo',
      },
    ],
  },
  {
    id: 'administracao',
    titulo: 'Administração',
    itens: [
      { id: 'equipe', rotulo: 'Equipe', para: '/equipe', icone: IconUsersGroup, capacidade: 'equipe.ver', descricao: 'A equipe da Resulti e os papéis' },
      { id: 'auditoria', rotulo: 'Auditoria', para: '/auditoria', icone: IconEye, capacidade: 'auditoria.ver', descricao: 'O que cada um fez, quando e de onde' },
      { id: 'servidores', rotulo: 'Servidores', para: '/servidores', icone: IconCpu, capacidade: 'servidores.ver', descricao: 'A saúde dos dois servidores da nuvem, os erros das APIs e os avisos no Slack' },
    ],
  },
];

/** Os grupos e itens que o papel alcança (grupo vazio some). */
export function menuDoPapel(pode: (c: Capacidade) => boolean): GrupoConsole[] {
  return GRUPOS.map((g) => ({ ...g, itens: g.itens.filter((i) => i.capacidade === null || pode(i.capacidade)) })).filter(
    (g) => g.itens.length > 0,
  );
}

/**
 * O item aceso para um caminho (a lista e tudo que vem embaixo dela). Vale a raiz MAIS LONGA que casa:
 * `/suporte/acessos` é o item "Acessos", não o "Clientes" de `/suporte` (que fica com `/suporte/clientes/:id`).
 */
export function itemDoCaminho(caminho: string): { grupo: GrupoConsole; item: ItemConsole } | null {
  let achado: { grupo: GrupoConsole; item: ItemConsole; tamanho: number } | null = null;
  for (const grupo of GRUPOS) {
    for (const item of grupo.itens) {
      for (const r of [item.para, ...(item.tambem ?? [])]) {
        if ((caminho === r || caminho.startsWith(`${r}/`)) && (!achado || r.length > achado.tamanho)) achado = { grupo, item, tamanho: r.length };
      }
    }
  }
  return achado ? { grupo: achado.grupo, item: achado.item } : null;
}

/**
 * Uma tela do console (para o `?volta=` do login): qualquer caminho interno que não seja o próprio login
 * nem o da API. Quem decide se a tela existe é o roteador (App.tsx).
 */
export function ehTelaDoConsole(caminho: string): boolean {
  return caminho !== LOGIN && caminho !== '/admin/v1' && !caminho.startsWith('/admin/v1/');
}
